import { normalizarTexto } from '../../../shared/utils/slug.util';
import { ResultadoBusca } from '../models/busca.model';
import { comecaComOTermo } from '../services/busca-publica.service';

/**
 * Uma página fixa do portal que a busca pode sugerir. A Ouvidoria e o e-SIC não são
 * registro de nenhuma tabela do catálogo, mas são o que o cidadão mais procura.
 */
export interface PaginaPortal {
  id: string;
  titulo: string;
  descricao: string;
  /** Rota declarada em `app-routing.module.ts`. */
  rota: string;
  /** O que o cidadão digita quando quer esta página, além do título. */
  palavrasChave: readonly string[];
}

/**
 * O catálogo das páginas fixas, no mesmo arranjo da busca do adm: registros vêm da API,
 * telas ficam no front. Uma página só aparece quando o termo casa com o título, a descrição
 * ou uma palavra-chave (`casarPaginas`), nunca de graça.
 */
export const PAGINAS_PORTAL: readonly PaginaPortal[] = [
  {
    id: 'ouvidoria',
    titulo: 'Ouvidoria',
    descricao:
      'Registre reclamações, denúncias, sugestões, elogios ou solicitações e acompanhe a resposta da prefeitura.',
    rota: '/ouvidoria',
    palavrasChave: ['manifestação', 'reclamação', 'denúncia', 'sugestão', 'elogio', 'ouvidor'],
  },
  {
    id: 'estatisticas-ouvidoria',
    titulo: 'Estatísticas da Ouvidoria',
    descricao: 'Os números das manifestações recebidas e respondidas pela Ouvidoria.',
    rota: '/estatisticas-ouvidoria',
    palavrasChave: ['estatística', 'indicadores', 'números'],
  },
  {
    id: 'sic',
    titulo: 'Acesso à informação (e-SIC)',
    descricao:
      'Peça informações públicas à prefeitura com base na Lei de Acesso à Informação e acompanhe o pedido.',
    rota: '/sic',
    palavrasChave: ['informação', 'lai', 'sic', 'e-sic', 'transparência', 'pedido de informação'],
  },
  {
    id: 'pedido-sic',
    titulo: 'Fazer um pedido de informação',
    descricao: 'O formulário do pedido de acesso à informação, identificado ou anônimo.',
    rota: '/sic/pedido',
    palavrasChave: ['pedido', 'informação', 'lai', 'anônimo'],
  },
  {
    id: 'estatisticas-sic',
    titulo: 'Estatísticas do e-SIC',
    descricao: 'Os números dos pedidos de informação recebidos e respondidos.',
    rota: '/sic/estatisticas',
    palavrasChave: ['estatística', 'indicadores', 'números'],
  },
  {
    id: 'acompanhamento',
    titulo: 'Acompanhamento de solicitação',
    descricao:
      'Consulte o andamento de um protocolo já aberto, com o histórico e a resposta da secretaria responsável.',
    rota: '/acompanhamento',
    palavrasChave: ['protocolo', 'andamento', 'consulta', 'acompanhar', 'status'],
  },
  {
    id: 'area-cidadao',
    titulo: 'Área do cidadão',
    descricao:
      'Seus dados, dependentes, serviços favoritos, agendamentos e solicitações num lugar só.',
    rota: '/area-cidadao',
    palavrasChave: ['meus dados', 'dependentes', 'favoritos', 'agendamento', 'reagendar', 'minhas solicitações', 'login', 'entrar'],
  },
  {
    id: 'cadastro',
    titulo: 'Cadastro no portal',
    descricao: 'Crie a sua conta para solicitar serviços e acompanhar os seus pedidos.',
    rota: '/cadastro',
    palavrasChave: ['cadastrar', 'criar conta', 'senha', 'registrar'],
  },
  {
    id: 'servicos',
    titulo: 'Todos os serviços',
    descricao: 'A lista completa dos serviços da prefeitura, organizada por categoria.',
    rota: '/servicos',
    palavrasChave: ['serviços', 'catálogo', 'carta de serviços', 'lista'],
  },
  {
    id: 'servicos-digitais',
    titulo: 'Serviços digitais',
    descricao: 'Os serviços que você resolve sem sair de casa.',
    rota: '/servicos-digitais',
    palavrasChave: ['online', 'digital', 'internet', 'sem sair de casa'],
  },
  {
    id: 'categorias-servicos',
    titulo: 'Categorias de serviços',
    descricao: 'Encontre o serviço pelo assunto que você precisa resolver.',
    rota: '/categorias-servicos',
    palavrasChave: ['categorias', 'assunto', 'secretarias'],
  },
  {
    id: 'noticias',
    titulo: 'Notícias',
    descricao: 'Comunicados, mudanças de horário de atendimento e novidades dos serviços municipais.',
    rota: '/noticias',
    palavrasChave: ['notícia', 'comunicado', 'novidades', 'campanha'],
  },
  {
    id: 'perguntas-frequentes',
    titulo: 'Perguntas frequentes',
    descricao: 'As dúvidas mais comuns sobre o portal e os serviços, já respondidas.',
    rota: '/perguntas-frequentes',
    palavrasChave: ['dúvida', 'faq', 'ajuda', 'como funciona'],
  },
  {
    id: 'politica-atendimento',
    titulo: 'Política de atendimento',
    descricao: 'Os compromissos e prazos do atendimento ao cidadão.',
    rota: '/politica-atendimento',
    palavrasChave: ['prazo', 'compromisso', 'atendimento'],
  },
  {
    id: 'outros-canais-atendimento',
    titulo: 'Outros canais de atendimento',
    descricao: 'Telefone, e-mail e atendimento presencial da prefeitura.',
    rota: '/outros-canais-atendimento',
    palavrasChave: ['telefone', 'e-mail', 'email', 'presencial', 'endereço', 'contato', 'fale conosco'],
  },
  {
    id: 'lgpd',
    titulo: 'Privacidade e proteção de dados (LGPD)',
    descricao: 'Como o portal trata os seus dados pessoais.',
    rota: '/lgpd',
    palavrasChave: ['privacidade', 'dados pessoais', 'lgpd', 'cookies'],
  },
];

const DESTAQUE_PAGINA = 'Página do portal';

/**
 * As páginas cujo título, descrição ou palavra-chave contém o termo, sem caixa nem acento.
 * Termo vazio não casa nada: nenhuma página aparece de graça.
 */
export function casarPaginas(termo: string, paginas: readonly PaginaPortal[] = PAGINAS_PORTAL): ResultadoBusca[] {
  const alvo = normalizarTexto(termo);

  if (!alvo) return [];

  return paginas
    .filter((pagina) =>
      [pagina.titulo, pagina.descricao, ...pagina.palavrasChave].some((texto) =>
        normalizarTexto(texto).includes(alvo),
      ),
    )
    .map((pagina) => ({
      id: `pagina-${pagina.id}`,
      canal: 'paginas',
      titulo: pagina.titulo,
      descricao: pagina.descricao,
      destaques: [DESTAQUE_PAGINA],
      link: pagina.rota,
      maisRelevante: comecaComOTermo(pagina.titulo, termo),
    }));
}
